import {
	FileSearchCorner,
	User,
	LayoutGrid,
	Phone,
	Mail,
	X,
} from 'lucide-react'
import { DateTime } from 'luxon'
import { Suspense, useState } from 'react'
import {
	Await,
	Link,
	Links,
	Meta,
	NavLink,
	Outlet,
	Scripts,
	ScrollRestoration,
	type ShouldRevalidateFunctionArgs,
	useLoaderData,
	useLocation,
} from 'react-router'
import Representative from '#app/assets/img/jvanguilder.jpeg'
import Logo from '#app/assets/svg/exzeoIQ.tsx'
import { GeneralErrorBoundary } from '#app/components/ErrorBoundary.tsx'
import LoginForm from '#app/components/LoginForm.tsx'
import LogoutForm from '#app/components/LogoutForm.tsx'
import styles from '#app/styles/tailwind.css?url'
import { getUser } from '#app/utils/auth.server.ts'
import { getEnv } from '#app/utils/env.server'
import { getReportTemplates } from '#app/utils/reports.ts'
import { type Route } from './+types/root'
import AtlasViewerIcon from './assets/svg/atlasViewerIcon'
import ClaimColonyIcon from './assets/svg/claimColonyIcon'
import HarmonyIcon from './assets/svg/harmonyIcon'

export const links: Route.LinksFunction = () => {
	return [{ rel: 'stylesheet', href: styles }]
}

export const meta: Route.MetaFunction = () => [
	{ title: 'ExzeoIQ' },
	{ name: 'description', content: 'Exzeo Carrier Reporting' },
]

export const loader = async ({ request, params }: Route.LoaderArgs) => {
	const user = await getUser(request)

	const reportTemplatesPromise = user?.accessToken
		? getReportTemplates({
				accessToken: user?.accessToken,
			})
		: []

	return {
		ENV: getEnv(),
		accessToken: user?.accessToken ?? '',
		carrier: params.carrier ?? '',
		userName: user?.name ?? '',
		reportTemplates: reportTemplatesPromise,
		canUpload: user?.canUpload ?? false,
	}
}

// Mock items - you can move these to your loader or a config file
const getPanelItems = (accessToken: string, userName: string) => [
	{
		id: 'item1',
		title: <LayoutGrid className="h-4 w-4" />,
		content: (
			<>
				<h4 className="text-xl font-normal text-accent">
					Contact / Exzeo Apps
				</h4>
				<div className="overflow-y-auto">
					<div
						id="resources"
						className="flex items-center gap-4 self-stretch rounded-[3px] p-4"
					>
						<img
							src={Representative}
							alt="Jessica Vanguilder"
							className="h-24 w-24 shrink-0 rounded-[3px] object-cover object-top"
						/>

						<div id="contactInfo" className="flex flex-col items-start gap-2">
							<h4 className="text-base text-accent">Jessica VanGuilder</h4>
							<p className="text-[11px] text-[#727272]">
								Client Engagement Representative
							</p>
							<a
								href="tel:+1-813-776-1107"
								className="flex items-center gap-1 text-[11px] text-primary"
							>
								<Phone className="h-3 w-3" />
								(813) 776-1107
							</a>
							<a
								href="mailto:jvanguilder@exzeo.com"
								className="flex items-center gap-1 text-[11px] text-primary"
							>
								<Mail className="h-3 w-3" />
								jvanguilder@exzeo.com
							</a>
						</div>
					</div>

					<hr />

					<ul className="flex flex-col gap-4">
						<a href="https://atlasviewer.com/" target="_blank">
							<li
								id="AtlasViewerLink"
								className="flex flex-grow items-center gap-2"
							>
								<span className="h-8 w-8">
									<AtlasViewerIcon />
								</span>
								<div className="flex flex-col items-start">
									<h4 className="text-[13px] text-accent">AtlasViewer</h4>
									<p className="text-[11px] text-base-content">
										Underwriting & Data Visualization
									</p>
								</div>
							</li>
						</a>
						<a href="https://claimcolony.net" target="_blank">
							<li
								id="ClaimColonyLink"
								className="flex flex-grow items-center gap-2"
							>
								<span className="h-8 w-8">
									<ClaimColonyIcon />
								</span>
								<div className="flex flex-col items-start">
									<h4 className="text-[13px] text-accent">ClaimColony</h4>
									<p className="text-[11px] text-base-content">
										Claims Processing Management
									</p>
								</div>
							</li>
						</a>
						<a href="https://agency.harmony-ins.com/" target="_blank">
							<li
								id="HarmonyLink"
								className="flex flex-grow items-center gap-2"
							>
								<span className="h-8 w-8">
									<HarmonyIcon />
								</span>

								<div className="flex flex-col items-start">
									<h4 className="text-[13px] text-accent">Harmony</h4>
									<p className="text-[11px] text-base-content">
										Underwriting & Policy Management
									</p>
								</div>
							</li>
						</a>
					</ul>

					{/* TODO: For future feature. */}
					<div id="recentReportsSection" className="flex hidden flex-col gap-4">
						<h2 className="whitespace-pre-line text-2xl font-normal leading-tight text-accent">
							Recent Reports
						</h2>

						<div
							id="recentReportsCard"
							className="card-body rounded-[3px] bg-[#FFFFFF] p-4 shadow-lg"
						>
							<div className="flex items-start">
								<div className="min-w-0 flex-1">
									<h2 className="whitespace-pre-line text-xl font-normal leading-tight text-accent">
										Policies In Force
									</h2>

									<div className="flex items-center justify-end gap-4">
										<span className="btn btn-primary btn-sm mt-4 items-center gap-2 rounded-[3px]">
											<FileSearchCorner className="h-4 w-4" />
											View Report
										</span>
									</div>
								</div>
							</div>
						</div>

						<div
							id="recentReportsCard"
							className="card-body rounded-[3px] bg-[#FFFFFF] p-4 shadow-lg"
						>
							<div className="flex items-start">
								<div className="min-w-0 flex-1">
									<h2 className="whitespace-pre-line text-xl font-normal leading-tight text-accent">
										Policies In Force
									</h2>

									<div className="flex items-center justify-end gap-4">
										<span className="btn btn-primary btn-sm mt-4 items-center gap-2 rounded-[3px]">
											<FileSearchCorner className="h-4 w-4" />
											View Report
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</>
		),
	},
	{
		id: 'item2',
		title: <User />,
		content: (
			<>
				<h4 className="text-xl font-normal text-accent">User Preferences</h4>

				<ul className="flex flex-col gap-4">
					<div
						id="userProfile"
						className="flex items-center items-stretch gap-4 p-4"
					>
						<User className="h-12 w-12 rounded-full bg-primary p-3 text-primary-content" />
						<div className="flex flex-col items-start gap-2">
							<h4 className="text-[17px] text-accent">{userName || ''}</h4>
							{/* TODO: add Carrier Context for logged-in user */}
							{/*
							<p className="text-[11px] text-base-content">Carrier Context</p> */}
						</div>
					</div>

					{/* TODO: Wire up Manage User link when we have a destination page. */}
					{/*
					<a href="#">
						<div
							id="manageUser"
							className="flex flex-grow items-center gap-2 p-3"
						>
							<User className="p-1" />
							<div className="flex flex-col items-start">
								<h4 className="text-[13px]">Manage user</h4>
							</div>
						</div>
					</a>
					*/}

					<hr />
					<li>
						{/* TODO: Wire up Privacy Policy link when we have a destination page. */}
						{/*
						<a href="#">
							<div
								id="privacyPolicy"
								className="flex flex-grow items-center gap-2 p-3"
							>
								<FileLock className="p-1" />
								<div className="flex flex-col items-start">
									<h4 className="text-[13px]">Privacy policy</h4>
								</div>
							</div>
						</a>
						*/}

						<a href="#">
							<div
								id="logout"
								className="flex flex-grow items-center gap-2 p-3"
							>
								<div className="flex flex-col items-start">
									{accessToken ? <LogoutForm /> : <LoginForm />}
								</div>
							</div>
						</a>
					</li>
				</ul>

				{/* TODO: For future feature. */}
				<div id="recentReportsSection" className="flex hidden flex-col gap-4">
					<h2 className="whitespace-pre-line text-2xl font-normal leading-tight text-accent">
						Recent Reports
					</h2>

					<div
						id="recentReportsCard"
						className="card-body rounded-[3px] bg-[#FFFFFF] p-4 shadow-lg"
					>
						<div className="flex items-start">
							<div className="min-w-0 flex-1">
								<h2 className="whitespace-pre-line text-xl font-normal leading-tight text-accent">
									Policies In Force
								</h2>

								<div className="flex items-center justify-end gap-4">
									<span className="btn btn-primary btn-sm mt-4 items-center gap-2 rounded-[3px]">
										<FileSearchCorner className="h-4 w-4" />
										View Report
									</span>
								</div>
							</div>
						</div>
					</div>

					<div
						id="recentReportsCard"
						className="card-body rounded-[3px] bg-[#FFFFFF] p-4 shadow-lg"
					>
						<div className="flex items-start">
							<div className="min-w-0 flex-1">
								<h2 className="whitespace-pre-line text-xl font-normal leading-tight text-accent">
									Policies In Force
								</h2>

								<div className="flex items-center justify-end gap-4">
									<span className="btn btn-primary btn-sm mt-4 items-center gap-2 rounded-[3px]">
										<FileSearchCorner className="h-4 w-4" />
										View Report
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</>
		),
	},
]

export default function App() {
	// Fallback to empty object if loader data is undefined (e.g. during error states)const data = useLoaderData() ?? {}const { ENV, accessToken, carrier, reportTemplates } = data
	const data = useLoaderData() ?? {}
	const { ENV, accessToken, carrier, userName, reportTemplates } = data
	const PANEL_ITEMS = getPanelItems(accessToken, userName)
	const { pathname } = useLocation()

	// Local state, not a search param: the panel is transient chrome, not a
	// selection worth deep-linking. Keeping it out of the URL avoids a router
	// navigation (and the loader revalidation / Loader flash / full-tree
	// re-render it triggers) on every open and close.
	const [activePanelId, setActivePanelId] = useState<string | null>(null)
	const isPanelOpen = Boolean(activePanelId)
	const openPanel = (id: string) =>
		setActivePanelId((current) => (current === id ? null : id))
	const closePanel = () => setActivePanelId(null)

	return (
		<html lang="en">
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<Meta />
				<Links />
			</head>
			<body
				data-theme="exzeo"
				className="theme-exzeo flex min-h-screen flex-col"
			>
				<header className="fixed top-0 z-50 w-full backdrop-blur supports-[backdrop-filter]:bg-base-100/40">
					<div className="flex h-16 items-center gap-4 bg-secondary px-1 px-4">
						{pathname.startsWith(`/carriers/${carrier}/reports`) && (
							<label
								htmlFor="sidebar"
								className="btn btn-ghost md:hidden"
								aria-label="open sidebar"
							>
								<svg
									width="24"
									height="24"
									viewBox="0 0 24 24"
									className="opacity-80"
								>
									<path fill="#fff" d="M3 6h18v2H3zm0 5h18v2H3zm0 5h18v2H3z" />
								</svg>
							</label>
						)}

						<Link to="/" className="logo dark">
							<Logo />
						</Link>
						{accessToken && (
							<>
								{/* TODO Wire up Carrier Dropdown to replace intial landing page when user has acccess to multiple carriers */}
								{/*
								<div
									id="carrierDropdown"
									className="dropdown dropdown-hover text-primary"
								>
									<label
										tabIndex={0}
										className="rounded-tl-0 -mb-[1px] flex cursor-pointer items-center gap-1 rounded-[3px] border-b-2 border-transparent bg-base-100 px-3 py-2 text-[14px] text-base-content/70 hover:text-primary"
									>
										Carrier Context <ChevronDown className="h-3 w-3" />
									</label>
									<ul
										tabIndex={0}
										className="menu dropdown-content z-50 w-52 rounded-[3px] bg-base-100 p-2 shadow"
									>
										<li>
											<NavLink
												to={`#`}
												className={({ isActive }) =>
													[
														'-mb-[1px] text-base-content',
														isActive
															? 'border-primary text-base-content'
															: 'border-transparent text-base-content/70 hover:text-primary',
													].join(' ')
												}
											>
												TTIC
											</NavLink>
										</li>
										<li>
											<NavLink
												to={`#`}
												className={({ isActive }) =>
													[
														'-mb-[1px] text-base-content',
														isActive
															? 'border-primary text-base-content'
															: 'border-transparent text-base-content/70 hover:text-primary',
													].join(' ')
												}
											>
												TIE
											</NavLink>
										</li>
										<li>
											<NavLink
												to={`#`}
												className={({ isActive }) =>
													[
														'-mb-[1px] text-base-content',
														isActive
															? 'border-primary text-base-content'
															: 'border-transparent text-base-content/70 hover:text-primary',
													].join(' ')
												}
											>
												PRAX
											</NavLink>
										</li>
										<li>
											<NavLink
												to={`#`}
												className={({ isActive }) =>
													[
														'-mb-[1px] text-base-content',
														isActive
															? 'border-primary text-base-content'
															: 'border-transparent text-base-content/70 hover:text-primary',
													].join(' ')
												}
											>
												HCPC
											</NavLink>
										</li>
										<li>
											<NavLink
												to={`#`}
												className={({ isActive }) =>
													[
														'-mb-[1px] text-base-content',
														isActive
															? 'border-primary text-base-content'
															: 'border-transparent text-base-content/70 hover:text-primary',
													].join(' ')
												}
											>
												CORE
											</NavLink>
										</li>
										<li>
											<NavLink
												to={`#`}
												className={({ isActive }) =>
													[
														'-mb-[1px] text-base-content',
														isActive
															? 'border-primary text-base-content'
															: 'border-transparent text-base-content/70 hover:text-primary',
													].join(' ')
												}
											>
												APEX
											</NavLink>
										</li>
									</ul>
								</div>
							*/}
								<nav className="ml-6 hidden flex-grow items-center gap-8 text-sm md:flex">
									{carrier && (
										<>
											<NavLink
												to={`/carriers/${carrier}/reports`}
												end={false}
												className={({ isActive }) =>
													[
														'-mb-[1px] border-b-2 text-white',

														isActive
															? 'border-primary text-base-content'
															: 'border-transparent text-base-content/70 hover:text-primary',
													].join(' ')
												}
											>
												Reports
											</NavLink>
											<NavLink
												to={`/carriers/${carrier}/dashboard`}
												className={({ isActive }) =>
													[
														'-mb-[1px] border-b-2 text-white',
														isActive
															? 'border-primary text-base-content'
															: 'border-transparent text-base-content/70 hover:text-primary',
													].join(' ')
												}
											>
												KPI Dashboard
											</NavLink>
										</>
									)}
									{/*<a
									href="https://atlasviewer.com"
									className="-mb-[1px] flex gap-1 border-b-2 border-transparent text-base-content/70 text-white hover:text-primary"
									rel="noopener noreferrer"
									target="_blank"
									>
									AtlasViewer
									<SquareArrowUpRightIcon className="h-4 w-4" />
									</a> */}

									<Suspense fallback={null}>
										<Await resolve={reportTemplates}>
											{(value) => (
												<>
													{value.length > 0 && ENV.FF_SHOW_ADMIN && (
														<NavLink
															to="/admin"
															className={({ isActive }) =>
																[
																	'-mb-[1px] border-b-2',

																	isActive
																		? 'border-primary text-base-content'
																		: 'border-transparent text-base-content/70 hover:text-primary',
																].join(' ')
															}
														>
															Admin
														</NavLink>
													)}
												</>
											)}
										</Await>
									</Suspense>
								</nav>

								<nav className="ml-6 hidden gap-4 text-sm md:flex md:items-center">
									{/* YOUR NEW TRIGGER BUTTONS */}
									{PANEL_ITEMS.map((item) => (
										<button
											key={item.id}
											type="button"
											onClick={() => openPanel(item.id)}
											className={`rounded-md bg-secondary px-1 py-1 text-primary-content transition-colors ${
												activePanelId === item.id
													? 'bg-accent text-primary-content'
													: 'text-primary-content hover:bg-primary'
											}`}
										>
											{item.title}
										</button>
									))}

									<div className="divider divider-horizontal m-0 h-4"></div>

									{/* ... (Existing Dashboard/Reports NavLinks) */}
								</nav>
							</>
						)}
					</div>
				</header>

				<section className="relative flex flex-grow justify-items-stretch pt-16 sm:h-[calc(100vh-24rem)] md:h-[calc(100vh-3rem)]">
					<Outlet />

					{/* THE SLIDING ASIDE PANEL */}
					<aside
						className={`fixed right-0 z-30 z-40 flex h-[calc(100vh-64px)] w-80 transform flex-col gap-6 overflow-hidden bg-base-100 p-4 shadow-2xl transition-transform duration-300 ease-in-out ${
							isPanelOpen ? 'translate-x-0' : 'translate-x-full'
						}`}
					>
						<button
							type="button"
							onClick={closePanel}
							className="btn btn-square btn-ghost btn-sm absolute right-0 top-0 mr-4 mt-3"
						>
							<X className="h-5 w-5" />
						</button>
						<>
							{PANEL_ITEMS.map((item) => (
								<div
									key={item.id}
									className={`flex flex-col gap-6 overflow-hidden transition-opacity duration-300 ${
										activePanelId === item.id
											? 'block opacity-100'
											: 'hidden opacity-0'
									}`}
								>
									{item.content}
								</div>
							))}
						</>
					</aside>

					{/* TODO: This is the new logout panel, need dev to toggle in/out. Currently has tailwind 'hidden' class.*/}
				</section>

				<footer className="flex h-24 flex-col items-center justify-center gap-2 bg-secondary p-4 text-secondary-content md:h-4">
					{accessToken && (
						<nav className="flex w-full justify-end gap-4 text-sm md:hidden">
							{carrier && (
								<div className="mr-auto flex items-center gap-4">
									<NavLink
										to={`/carriers/${carrier}/reports`}
										className={({ isActive }) =>
											[
												'-mb-[1px] border-b-2',

												isActive
													? 'btn btn-primary btn-sm'
													: 'btn btn-outline btn-primary btn-sm',
											].join(' ')
										}
									>
										Reports
									</NavLink>
									<NavLink
										to={`/carriers/${carrier}/dashboard`}
										className={({ isActive }) =>
											[
												'-mb-[1px] border-b-2',
												isActive
													? 'btn btn-primary btn-sm'
													: 'btn btn-outline btn-primary btn-sm',
											].join(' ')
										}
									>
										KPI Dashboard
									</NavLink>
								</div>
							)}
							{/* YOUR NEW TRIGGER BUTTONS */}
							<div className="flex items-center gap-4 text-sm">
								{PANEL_ITEMS.map((item) => (
									<button
										key={item.id}
										type="button"
										onClick={() => openPanel(item.id)}
										className={`rounded-md bg-secondary px-1 py-1 text-primary-content transition-colors ${
											activePanelId === item.id
												? 'bg-accent text-primary-content'
												: 'text-primary-content hover:bg-primary'
										}`}
									>
										{item.title}
									</button>
								))}
							</div>
						</nav>
					)}
					<span id="copyright" className="text-xs">
						&copy;ExzeoIQ {DateTime.now().year}
					</span>
				</footer>

				<script
					suppressHydrationWarning
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(ENV)}`,
					}}
				/>
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	)
}

export const ErrorBoundary = GeneralErrorBoundary

export const shouldRevalidate = ({
	currentParams,
	nextParams,
}: ShouldRevalidateFunctionArgs) => {
	return currentParams.carrier !== nextParams.carrier
}
